import type { Place } from '@/utils/autoComplete';

// Loads the Google Places Autocomplete widget via a plain injected <script>
// tag (same pattern as chatBotManager.ts) rather than an npm wrapper — avoids
// adding a dependency for a single widget and keeps the ambient surface small.
declare global {
  interface Window {
    // Maps JS calls this instead of rendering its own error banner when key
    // authentication fails. It does NOT stop the API from disabling the input
    // the Autocomplete widget is attached to, so a handler still has to repair
    // the field (see usePlacesAutocomplete).
    gm_authFailure?: () => void;
    google?: {
      maps: {
        // Defined by the `loading=async` bootstrap. Resolves once the named
        // library is actually attached to `google.maps`.
        importLibrary?: (library: string) => Promise<unknown>;
        event?: {
          clearInstanceListeners: (instance: object) => void;
        };
        places: {
          Autocomplete: new (
            input: HTMLInputElement,
            options?: GoogleAutocompleteOptions,
          ) => GoogleAutocomplete;
        };
      };
    };
  }
}

export interface GoogleAutocompleteOptions {
  componentRestrictions?: { country: string | string[] };
  fields?: string[];
  types?: string[];
}

export interface GoogleAutocomplete {
  addListener(eventName: 'place_changed', handler: () => void): void;
  // Reuses the existing Place shape (utils/autoComplete.ts) so a selected
  // place feeds getAddressComponentFromPlace/buildStreetAddressLine directly,
  // with no separate/incompatible Google-specific type to keep in sync.
  getPlace(): Place;
}

const SCRIPT_ID = 'google-maps-places-script';

let loadPromise: Promise<void> | null = null;

/** Idempotent: safe to call from multiple mounted components — injects the
 * script at most once and every caller awaits the same promise. On rejection
 * the memo is cleared so a later remount can retry after a transient failure. */
export const loadGoogleMapsPlaces = (apiKey: string): Promise<void> => {
  if (window.google?.maps?.places) return Promise.resolve();
  if (loadPromise) return loadPromise;

  loadPromise = new Promise<void>((resolve, reject) => {
    const existing = document.getElementById(SCRIPT_ID);
    if (existing) {
      existing.addEventListener('load', () => resolve());
      existing.addEventListener('error', () => reject(new Error('Failed to load Google Maps script')));
      return;
    }

    // The script's `load` event only means the bootstrap parsed — under
    // `loading=async` the places library (and even `google.maps.importLibrary`)
    // attaches a few hundred ms later, so resolving on `onload` hands callers a
    // `google.maps.places` that is still undefined. `callback=` is the bootstrap's
    // own "libraries are ready" signal; that is what we resolve on.
    const callbackName = '__hhGoogleMapsReady';
    (window as unknown as Record<string, () => void>)[callbackName] = () => {
      delete (window as unknown as Record<string, unknown>)[callbackName];
      resolve();
    };

    const script = document.createElement('script');
    script.id = SCRIPT_ID;
    // loading=async matches Google's current Maps JS bootstrap guidance.
    script.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(apiKey)}&libraries=places&loading=async&callback=${callbackName}`;
    script.async = true;
    script.onerror = () => reject(new Error('Failed to load Google Maps script'));
    document.head.appendChild(script);
  })
    // Belt and braces: a bootstrap that fires the callback before places is
    // attached still has `importLibrary` by then, so await it rather than
    // handing ShippingStep an undefined `places`.
    .then(async () => {
      if (window.google?.maps?.places) return;
      const importLibrary = window.google?.maps?.importLibrary;
      if (importLibrary) await importLibrary('places');
      if (!window.google?.maps?.places) {
        throw new Error('Google Maps places library unavailable after load');
      }
    })
    .then(
      () => undefined,
      (err: unknown) => {
        loadPromise = null;
        return Promise.reject(err);
      },
    );

  return loadPromise;
};
